import React, { useMemo } from 'react';
import { Select, Tag, Tooltip } from 'antd';
import { useTranslation } from '../i18n';

/**
 * 多值输入（标签）：逗号 / 中文逗号 / 分号 / 换行（可选空格）分隔即成标签，粘贴多行自动拆分。
 *
 * 表单里存的值格式保持不变，提交逻辑不用改：
 *   format="comma"  → 'a,b,c'       （默认按初始值自动判断）
 *   format="lines"  → 'a\nb\nc'
 *   format="array"  → ['a','b','c']
 * commaSeparates={false}：只按换行/回车分隔（值本身可能含逗号，如正则）
 * validate(v) 返回错误文案（字符串）则该标签标红并提示。
 * tagPrefix(v) 返回标签前缀节点(如国家旗帜)。
 */
const split = (raw, commas = true) => String(raw ?? '').split(commas ? /[\n,，;；]+/ : /\n+/).map((x) => x.trim()).filter(Boolean);

const toList = (value, commas) => (Array.isArray(value) ? value.map((x) => String(x).trim()).filter(Boolean) : split(value, commas));

const fromList = (list, format) => {
    if (format === 'array') return list;
    if (format === 'lines') return list.join('\n');
    return list.join(',');
};

const TagsInput = ({
    value,
    onChange,
    format,
    placeholder,
    validate,
    spaceSeparates = true,
    commaSeparates = true,
    lowercase = false,
    uppercase = false,
    disabled,
    style,
    maxTagTextLength = 60,
    tagPrefix,
    ...rest
}) => {
    const { t } = useTranslation();
    const fmt = format || (Array.isArray(value) ? 'array' : (typeof value === 'string' && value.includes('\n') ? 'lines' : 'comma'));
    const list = useMemo(() => toList(value, commaSeparates), [value, commaSeparates]);
    const separators = [...(commaSeparates ? [',', '，', ';', '；'] : []), '\n', ...(spaceSeparates ? [' '] : [])];

    const handleChange = (vals) => {
        const seen = new Set();
        const next = [];
        vals.flatMap((v) => (spaceSeparates ? split(v, commaSeparates).flatMap((x) => x.split(/\s+/)) : split(v, commaSeparates)))
            .map((v) => (lowercase ? v.toLowerCase() : uppercase ? v.toUpperCase() : v))
            .filter(Boolean)
            .forEach((v) => { if (!seen.has(v)) { seen.add(v); next.push(v); } });
        onChange && onChange(fromList(next, fmt));
    };

    const tagRender = ({ value: v, closable, onClose }) => {
        const err = validate ? validate(v) : null;
        const tag = (
            <Tag
                color={err ? 'error' : undefined}
                closable={closable && !disabled}
                onClose={onClose}
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                style={{ marginInlineEnd: 4, marginBlock: 2 }}
            >
                {tagPrefix ? tagPrefix(v) : null}
                {String(v).length > maxTagTextLength ? `${String(v).slice(0, maxTagTextLength)}…` : v}
            </Tag>
        );
        return err ? <Tooltip title={err}>{tag}</Tooltip> : tag;
    };

    return (
        <Select
            mode="tags"
            value={list}
            onChange={handleChange}
            tokenSeparators={separators}
            open={false}
            suffixIcon={null}
            placeholder={placeholder || t('Press Enter after typing or separate with commas')}
            tagRender={tagRender}
            disabled={disabled}
            style={{ width: '100%', ...style }}
            {...rest}
        />
    );
};

/** 常用校验 */
const isIPv4 = (ip) => /^(\d{1,3}\.){3}\d{1,3}$/.test(ip) && ip.split('.').every((o) => Number(o) <= 255 && String(Number(o)) === o);
const isIPv6 = (ip) => ip.includes(':') && /^[0-9a-f:.]+$/i.test(ip) && (ip.match(/::/g) || []).length <= 1 && ip.split(':').length <= 8;
export const isIpOrCidr = (v) => {
    const [ip, bits, extra] = String(v).split('/');
    if (extra !== undefined) return false;
    const v4 = isIPv4(ip);
    if (!v4 && !isIPv6(ip)) return false;
    if (bits === undefined) return true;
    return /^\d{1,3}$/.test(bits) && Number(bits) <= (v4 ? 32 : 128);
};
export const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
export const isOrigin = (v) => v === '*' || /^https?:\/\/(\*\.)?[a-z0-9-]+(\.[a-z0-9-]+)*(:\d{1,5})?$/i.test(v) || /^https?:\/\/\[[0-9a-f:]+\](:\d{1,5})?$/i.test(v);
export const isHeaderName = (v) => /^[!#$%&'*+.^_`|~0-9A-Za-z-]+$/.test(v);
export const isCookieName = isHeaderName;
export const isQueryParam = (v) => /^[^\s&=#]+$/.test(v);
export const isCountryCode = (v) => /^[A-Z]{2}$/i.test(v);
export const isRegionCode = (v) => /^[A-Z0-9]{1,3}(-[A-Z0-9]{1,3})?$/i.test(v);
export const isAsn = (v) => /^\d{1,10}$/.test(v);
export const isUrlOrPath = (v) => /^(https?:\/\/[^\s/]+(\/\S*)?|\/\S*)$/i.test(v);
export const isExtension = (v) => /^\.?[a-z0-9]{1,16}$/i.test(v);
export const isDomainPattern = (v) => /^(\*\.)?([a-z0-9-]+\.)+[a-z0-9-]{2,}$/i.test(v) || v === '*';

export default TagsInput;
